import React, { useState, forwardRef } from "react";
// import FlipMove from "react-flip-move";
import ProfileContainer from "../components/ProfileContainer";


const MAX_LENGTH = 10;


export const SparkleIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z" fill="#7c3aed" stroke="#7c3aed" strokeWidth="1" strokeLinejoin="round" />
        <path d="M19 14L19.54 16.73L22 17L19.54 17.27L19 20L18.46 17.27L16 17L18.46 16.73L19 14Z" fill="#a78bfa" stroke="#a78bfa" strokeWidth="0.5" strokeLinejoin="round" />
        <path d="M5 3L5.36 4.82L7 5L5.36 5.18L5 7L4.64 5.18L3 5L4.64 4.82L5 3Z" fill="#a78bfa" stroke="#a78bfa" strokeWidth="0.5" strokeLinejoin="round" />
    </svg>
);

const SubScoreBar = ({ label, value }) => {
    const pct = (value / 10) * 100;
    const barColor = value >= 8 ? "bg-emerald-500"
        : value >= 6 ? "bg-amber-400"
            : "bg-gray-300";

    return (
        <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-32 shrink-0">{label}</span>
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-semibold w-6 text-right text-gray-700">
                {`${value}`}
            </span>
        </div>
    );
};

const RedFlagBanner = ({ penalty }) => {
    if (!penalty || penalty === 0) return null;
    return (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className="text-xs text-red-600 font-medium leading-relaxed">
                Red flags detected — Review your resume for inconsistencies or missing information.
            </p>
        </div>
    );
};

const SUBSCORE_LABELS = {
    title_match: "Title Match",
    skills_depth: "Skills Depth",
    experience_relevance: "Experience",
    resume_quality: "Resume Quality",
    red_flags_penalty: "Red Flags",
};

// reused component
export const BreakdownPanel = ({ discipline }) => {
    const { pros = [], cons = [], subscores = {} } = discipline.resume_match_breakdown || {};
    const hasPros = pros.length > 0;
    const hasCons = cons.length > 0;

    return (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 px-3 py-2.5 space-y-4">
            {/* Subscores */}
            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Score Breakdown</p>
                <div className="space-y-2">
                    {Object.entries(subscores).map(([key, val]) => key === "red_flags_penalty" ? null : (
                        <SubScoreBar key={key} label={SUBSCORE_LABELS[key] || key} value={val} />
                    ))}
                </div>
            </div>

            <RedFlagBanner penalty={subscores.red_flags_penalty} />

            {(hasPros || hasCons) && (
                <div className={`grid gap-3 ${hasPros && hasCons ? "grid-cols-2" : "grid-cols-1"}`}>
                    {hasPros && (
                        <div>
                            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                                Strengths
                            </p>
                            <ul className="space-y-1.5">
                                {pros.map((p, i) => (
                                    <li key={i} className="text-xs text-gray-600 leading-relaxed flex gap-1.5">
                                        <span className="text-emerald-400 mt-0.5 shrink-0">•</span>{p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {hasCons && (
                        <div>
                            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                Gaps
                            </p>
                            <ul className="space-y-1.5">
                                {cons.map((c, i) => (
                                    <li key={i} className="text-xs text-gray-600 leading-relaxed flex gap-1.5">
                                        <span className="text-amber-400 mt-0.5 shrink-0">•</span>{c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {!hasPros && !hasCons && (
                <p className="text-xs text-gray-500 italic">No specific strengths or gaps identified — resume aligns strongly across all evaluated dimensions.</p>
            )}

            <p className="text-xs text-gray-400 flex items-center gap-1.5 pt-1">
                <SparkleIcon />
                AI-generated analysis based on the talent's profile
            </p>
        </div>
    );
};

/* ─── Pill View ─────────────────────────────────────────────── */

const RolePillItem = forwardRef(({ discipline, isOpen, onToggle }, ref) => {
    const hasBreakdown = !!discipline.resume_match_breakdown;
    const selfScoreLabel = discipline.self_score
        ? laravel.talent_scoring_options?.[discipline.self_score] || discipline.self_score
        : null;
    const resumeScore = discipline.resume_match_score;
    const scoreOutOf10 = resumeScore ? (resumeScore / 10).toFixed(1) : null;

    return (
        <div ref={ref} style={{ width: isOpen ? '100%' : 'auto', transition: 'width 0.3s ease-out' }}>
            <div
                className={TB.mapClass(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all",
                    {
                        "border-violet-400 bg-violet-50 shadow-sm": isOpen,
                        "border-gray-300 bg-white hover:border-violet-300 hover:bg-violet-50/50": !isOpen,
                    }
                )}
                onClick={() => hasBreakdown && onToggle()}
            >
                <span className={`text-sm font-medium ${isOpen ? 'text-violet-700' : 'text-gray-800'}`}>
                    {discipline.name}
                </span>
                {selfScoreLabel && (
                    <span className="text-xs text-gray-400">
                        {selfScoreLabel}
                    </span>
                )}
                {scoreOutOf10 && (
                    <span className="flex items-center gap-1 text-xs text-violet-600 font-semibold">
                        <SparkleIcon />
                        {scoreOutOf10}
                    </span>
                )}
                {hasBreakdown && (
                    <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        className="text-gray-400 shrink-0"
                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                )}
            </div>

            {isOpen && hasBreakdown && (
                <div className="mt-3">
                    <BreakdownPanel discipline={discipline} />
                </div>
            )}
        </div>
    );
});

/* ─── Card View (original) ──────────────────────────────────── */

const RoleCard = ({ discipline }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasBreakdown = !!discipline.resume_match_breakdown;

    const selfScoreLabel = discipline.self_score
        ? laravel.talent_scoring_options?.[discipline.self_score] || discipline.self_score
        : null;

    const resumeScore = discipline.resume_match_score;
    const scoreOutOf10 = resumeScore ? (resumeScore / 10).toFixed(1) : null;

    return (
        <div className="py-4">
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => hasBreakdown && setIsOpen(!isOpen)}
            >
                <div className="flex flex-col gap-1">
                    <h5 className="text-base font-semibold text-gray-900">{discipline.name}</h5>
                    {selfScoreLabel && (
                        <p className="text-xs text-gray-700">
                            {selfScoreLabel}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {scoreOutOf10 && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-violet-200 bg-violet-50">
                            <SparkleIcon />
                            <span className="text-xs text-violet-500 font-medium">Resume Score</span>
                            <span className="text-xs font-bold text-violet-700">
                                {scoreOutOf10}<span className="font-normal text-violet-400">/10</span>
                            </span>
                        </div>
                    )}
                    {hasBreakdown && (
                        <svg
                            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            className="text-gray-400"
                            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    )}
                </div>
            </div>

            {hasBreakdown && (
                <div style={{ maxHeight: isOpen ? 600 : 0, opacity: isOpen ? 1 : 0, overflow: "hidden", transition: "max-height 0.35s ease, opacity 0.25s ease" }}>
                    <div className="mt-3">
                        <BreakdownPanel discipline={discipline} />
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── Main Section ──────────────────────────────────────────── */

const TalentProfileRoles = ({ items = [] }) => {
    const [isMoreOpen, setIsMoreOpen] = useState(false);

    // const [openPillId, setOpenPillId] = useState(null);
    // const handlePillToggle = (id) => {
    //     setOpenPillId(prev => prev === id ? null : id);
    // };

    return (
        <div id="roles" className="pt-2 pb-8">
            <ProfileContainer>
                <div>
                    <h4 className="textbold text-lg pb-6">Roles {!!items.length && `(${items.length})`}</h4>

                    {!items.length && (
                        <div className="py-8 text-center">
                            <p className="text-base">No Roles Available</p>
                        </div>
                    )}

                    {/* Pill View */}
                    {/* {!!items.length && (
                        <FlipMove
                            className="flex flex-row flex-wrap gap-3 items-start"
                            duration={300}
                            easing="ease-out"
                            staggerDurationBy={30}
                            enterAnimation="fade"
                            leaveAnimation="fade"
                        >
                            {items.map(discipline => (
                                <RolePillItem
                                    key={discipline.id}
                                    discipline={discipline}
                                    isOpen={openPillId === discipline.id}
                                    onToggle={() => handlePillToggle(discipline.id)}
                                />
                            ))}
                        </FlipMove>
                    )}

                    <div className="bg-red-300 p-2"></div> */}

                    {/* Card List View */}
                    {!!items.length && (
                        <div className="flex flex-col divide-y mt-6 border-t">
                            {/* {items.map(discipline => ( */}
                            {items?.slice(0, isMoreOpen ? items?.length : MAX_LENGTH).map(discipline => (
                                <RoleCard key={discipline.id} discipline={discipline} />
                            ))}
                        </div>
                    )}

                    {items?.length > MAX_LENGTH && (
                        <div className="pt-6 flex justify-end">
                            <button
                                type="button"
                                className="btn-outline flex items-center gap-2 p-1.5 px-2.5 transition hover:bg-wrippleOffWhite"
                                onClick={() => {
                                    setIsMoreOpen(!isMoreOpen)
                                    if (isMoreOpen) {
                                        const element = document.getElementById("roles");
                                        element.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }}
                            >
                                <p>{items?.length - MAX_LENGTH} {isMoreOpen ? 'less' : 'more'}</p>
                                <div className="bg-wrippleOffWhite h-6 w-6 shrink-0 flex items-center justify-center">
                                    <i className={TB.mapClass("fa fa-chevron-down transition text-base", { 'rotate-180': isMoreOpen })} />
                                </div>
                            </button>
                        </div>
                    )}
                </div>
            </ProfileContainer>
        </div>
    );
};

export default TalentProfileRoles;